/**
 * @file CabinetPageHeader.tsx
 * @description Заголовок страницы в верхней панели кабинета (phase19.29;
 * phase19.31 шаг О4 — статус, «назад», «⋯», общая нижняя полоса, SC33, SC34).
 *
 * Страница объявляет, что показать, — компонент раскладывает по местам:
 *  - широкий экран: в панели слева «назад», название, статус-пилюля и под
 *    ними пояснение; справа — второстепенные действия (не больше двух,
 *    кнопки-контуры), «⋯» с остальными и ОДНО главное действие;
 *  - сжатый заголовок (порог задаёт каркас: платформа — до 768, магазин —
 *    до 1024): в панели «назад», название и «⋯» (туда же уходят
 *    второстепенные действия); пояснение и статус — первой строкой
 *    содержимого; главное действие — в общей нижней полосе каркаса (не
 *    плавающая кнопка: каркас резервирует под полосу место).
 * Название вкладки браузера объявляется каркасу (`claimTitle`), каркас
 * ставит «<Название> — <суффикс>».
 *
 * Места в панели даёт каркас (`cabinet-header-slots`). Без каркаса с местами
 * компонент ничего не рисует (и предупреждает в консоли разработки).
 *
 * ```tsx
 * <CabinetPageHeader
 *   title="Обзор склада"
 *   subtitle="Основной склад · сегодня, 2 октября"
 *   action={<Button variant="brand" asChild><Link to="receipts/new">Приёмка</Link></Button>}
 *   secondary={[{ key: "refresh", label: "Обновить", icon: RefreshCw, run: reload }]}
 *   menu={[{ key: "writeoff", label: "Новое списание", icon: Trash2, to: "writeoffs/new" }]}
 * />
 * ```
 */

import { useLayoutEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";

import { WIDE_QUERY, useMediaQuery } from "../../hooks/useMediaQuery";
import { useAppShellOptional } from "./AppShell";
import { headerPlacement, type CabinetPageHeaderProps } from "./cabinet-header-placement";
import { CabinetBottomBar, DocCodeText, PageActionButton, PageActionsMenu, PageBackButton } from "./cabinet-page-header-parts";
import { useCabinetHeaderSlots } from "./cabinet-header-slots";
import { cn } from "@/lib/utils";

export function CabinetPageHeader({
  title,
  subtitle,
  status,
  back,
  action,
  secondary = [],
  menu = [],
  menuLabel = "Другие действия",
  mobileAction,
  mobileNote,
  secondaryActions,
  secondaryBelow = false,
  panelActions,
}: CabinetPageHeaderProps) {
  const slots = useCabinetHeaderSlots();
  const shell = useAppShellOptional();
  const compactBelow = slots?.compactBelow ?? "md";
  // Порог «сжатого» заголовка: у магазина он совпадает со шторкой меню (< 1024).
  const compact = compactBelow === "lg" ? (shell?.isMobile ?? false) : (shell?.isPhone ?? false);
  const wide = useMediaQuery(WIDE_QUERY);
  const claimTitle = slots?.claimTitle;

  useLayoutEffect(() => (claimTitle ? claimTitle(title) : undefined), [title, claimTitle]);

  if (!slots) {
    // Ошибка сборки страницы: заголовок кабинета вне каркаса с местами.
    if (import.meta.env.DEV) console.warn("CabinetPageHeader: каркас не предоставил места в верхней панели");
    return null;
  }

  const wideSecondary = secondary.slice(0, 2);
  // Узкий экран: второстепенные — пунктами «⋯» (кроме тех, что уже в полосе).
  const menuItems = compact
    ? [...secondary.filter((a) => a.compact !== "hide"), ...menu]
    : [...secondary.slice(2), ...menu];
  const bottom = mobileAction ?? action;
  // Готовые кнопки: панель или строка под шапкой — по ширине (название важнее).
  const place = headerPlacement({
    compact,
    wide,
    secondaryBelow,
    hasPanelActions: !!panelActions,
    hasSecondaryActions: !!secondaryActions,
  });
  const panelReady = place.panelActionsInPanel || place.secondaryInPanel;
  const rowReady = place.panelActionsInRow || place.secondaryInRow;
  const showRow = compact ? !!(subtitle || status || rowReady) : rowReady;

  return (
    <>
      {slots.startEl
        ? createPortal(
            <PageTitle
              title={title}
              subtitle={compact ? undefined : subtitle}
              status={compact ? undefined : status}
              back={back}
              wrap={compact}
            />,
            slots.startEl,
          )
        : null}
      {slots.endEl && (menuItems.length > 0 || (!compact && (action || panelReady || wideSecondary.length > 0)))
        ? createPortal(
            <div className="flex items-center gap-2">
              {place.secondaryInPanel ? secondaryActions : null}
              {place.panelActionsInPanel ? panelActions : null}
              {compact ? null : wideSecondary.map((a) => <PageActionButton key={a.key} action={a} />)}
              {menuItems.length > 0 ? <PageActionsMenu actions={menuItems} label={menuLabel} /> : null}
              {compact ? null : action}
            </div>,
            slots.endEl,
          )
        : null}
      {showRow ? (
        <div className="flex flex-col gap-3">
          {compact && (status || subtitle) ? (
            <div className="flex flex-wrap items-center gap-2">
              {status}
              {subtitle ? <p className="type-subtitle text-on-surface-variant">{subtitle}</p> : null}
            </div>
          ) : null}
          {rowReady ? (
            <div className="flex flex-wrap gap-2">
              {place.panelActionsInRow ? panelActions : null}
              {place.secondaryInRow ? secondaryActions : null}
            </div>
          ) : null}
        </div>
      ) : null}
      {compact && bottom && slots.bottomEl
        ? createPortal(
            <CabinetBottomBar note={mobileNote} hideFrom={compactBelow}>
              {bottom}
            </CabinetBottomBar>,
            slots.bottomEl,
          )
        : null}
    </>
  );
}

interface TitleProps {
  title: string;
  subtitle?: string;
  status?: ReactNode;
  back?: { to: string; label: string };
  /**
   * Сжатый заголовок (телефон, планшет): название переносится на вторую
   * строку, многоточие — только если не влезло и в две (дефект приёмки
   * 2026-10-02: на 360 «Задания на сбо…»). Пояснения в панели тогда нет —
   * двум строкам названия хватает высоты шапки.
   */
  wrap?: boolean;
}

/** Название (единственный h1 страницы), пояснение, статус и «назад». */
function PageTitle({ title, subtitle, status, back, wrap = false }: TitleProps) {
  return (
    <div className="flex min-w-0 items-center gap-2">
      {back ? <PageBackButton to={back.to} label={back.label} /> : null}
      <div className="flex min-w-0 flex-col">
        <div className="flex min-w-0 items-center gap-2">
          {/* Не уже 6rem: длинные статусы и кнопки не сжимают название до нуля (QA конструктора на 1024). */}
          <h1 title={title} className={cn("type-page-title text-on-surface", wrap ? "line-clamp-2 break-words" : "min-w-24 truncate")}>
            {/* Сжатый заголовок: номер документа переносится только после года. */}
            {wrap ? <DocCodeText title={title} /> : title}
          </h1>
          {status ? <span className="shrink-0">{status}</span> : null}
        </div>
        {subtitle ? (
          <p title={subtitle} className="type-subtitle truncate text-on-surface-variant">
            {subtitle}
          </p>
        ) : null}
      </div>
    </div>
  );
}
